import { Box, Container, styled, useMediaQuery, useTheme } from '@mui/material';
import { JobSeekingStatus } from 'types/types';
import { useConnectContext } from '../../Context/ConnectContextProvider';
import { useState, useEffect } from 'react';
import { MatchArchiveFilterButtonGroup } from 'features/Connect/components/Buttons/MatchArchiveFilterButtonGroup';
import { MatchArchiveFilterTypes } from './utils/constants';
import { CandidateMatchArchiveDashboardHeader } from './CandidateMatchArchiveDashboardHeader';
import { CandidateMatchArchiveDashboardBody } from './CandidateMatchArchiveDashboardBody';
import { MatchArchiveFilterType } from './utils/types';
import { ConnectCandidateMatchArchiveDashboardDataTestIds } from 'data-testids/CandidatePortalDataTestIds';
import {
  getFilteredArchivedMatches,
  getTotalArchivedMatches,
  hasAcceptedMatches,
  hasRejectedMatches,
  shouldShowDashboard,
  getDashboardViewState,
} from './utils/utils';

export const CandidateMatchArchiveDashboard: React.FC = () => {
  const { candidateMatches, hasInitiallyFetchedMatches, isFetchingMatches, jobSeekingStatus } =
    useConnectContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isUserOpenToWorkAndMatches = jobSeekingStatus === JobSeekingStatus.OPEN_TO_WORK_AND_MATCHES;
  const [activeFilter, setActiveFilter] = useState<MatchArchiveFilterType>(
    MatchArchiveFilterTypes.ALL
  );

  const filteredArchivedMatches = getFilteredArchivedMatches(candidateMatches, activeFilter);
  const totalArchivedMatches = getTotalArchivedMatches(candidateMatches);
  const hasAcceptedMatchesValue = hasAcceptedMatches(candidateMatches);
  const hasRejectedMatchesValue = hasRejectedMatches(candidateMatches);
  const shouldShowDashboardValue = shouldShowDashboard(totalArchivedMatches);

  const dashboardViewState = getDashboardViewState(
    isUserOpenToWorkAndMatches,
    isFetchingMatches,
    hasInitiallyFetchedMatches,
    shouldShowDashboardValue,
    filteredArchivedMatches.length
  );

  const handleFilterChange = (filter: MatchArchiveFilterType) => {
    setActiveFilter(filter);
  };

  useEffect(() => {
    if (activeFilter === MatchArchiveFilterTypes.INTERESTED && !hasAcceptedMatchesValue) {
      setActiveFilter(MatchArchiveFilterTypes.ALL);
    } else if (activeFilter === MatchArchiveFilterTypes.NO_THANKS && !hasRejectedMatchesValue) {
      setActiveFilter(MatchArchiveFilterTypes.ALL);
    }
  }, [activeFilter, hasAcceptedMatchesValue, hasRejectedMatchesValue]);

  return (
    <MatchArchiveDashboardContainer
      data-testid={ConnectCandidateMatchArchiveDashboardDataTestIds.DASHBOARD_CONTAINER}
    >
      <CandidateMatchArchiveDashboardHeader
        isUserOpenToWorkAndMatches={isUserOpenToWorkAndMatches}
        hasInitiallyFetchedMatches={hasInitiallyFetchedMatches}
        shouldShowDashboard={shouldShowDashboardValue}
        filteredArchivedMatchesLength={filteredArchivedMatches.length}
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        hasAcceptedMatches={hasAcceptedMatchesValue}
        hasRejectedMatches={hasRejectedMatchesValue}
      />

      <CandidateMatchArchiveDashboardBody
        viewState={dashboardViewState}
        filteredArchivedMatches={filteredArchivedMatches}
      />
      {isMobile && shouldShowDashboardValue && (
        <MatchArchiveMobileButtonGroupContainer
          data-testid={
            ConnectCandidateMatchArchiveDashboardDataTestIds.MOBILE_BUTTON_GROUP_CONTAINER
          }
        >
          <MatchArchiveFilterButtonGroup
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
            hasAcceptedMatches={hasAcceptedMatchesValue}
            hasRejectedMatches={hasRejectedMatchesValue}
          />
        </MatchArchiveMobileButtonGroupContainer>
      )}
    </MatchArchiveDashboardContainer>
  );
};

const MatchArchiveDashboardContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  height: '100%',
  width: '100%',
  maxWidth: '1040px',
  padding: theme.spacing(3, 0, 8, 0),

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1, 0, 8, 0),
  },
}));

const MatchArchiveMobileButtonGroupContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  width: '100%',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.white.main,
  zIndex: 1000,
  borderTop: `1px solid ${theme.palette.border.main}`,
}));
